import JSZip from 'jszip';
import { supabase } from '@/integrations/supabase/client';

export type WriterSourceExport = {
  versionId: string;
  projectId: string;
  projectTitle: string;
  label: string | null;
  mainTex: string;
  bibSource: string;
  compileStatus: string;
  createdAt: string;
};

export class SourceExportDenied extends Error {
  constructor(
    public readonly kind:
      | 'upgrade_required'
      | 'forbidden'
      | 'not_found'
      | 'unavailable'
      | 'unknown',
  ) {
    super(kind);
    this.name = 'SourceExportDenied';
  }
}

/** Fetches one version's LaTeX source through the entitlement-gated RPC. */
export async function fetchWriterSource(versionId: string): Promise<WriterSourceExport> {
  const { data, error } = await supabase.rpc('writer_export_source', {
    p_version_id: versionId,
  });

  if (error) {
    const message = `${error.message ?? ''}`;
    const code = `${(error as { code?: string }).code ?? ''}`;
    if (message.includes('upgrade_required')) throw new SourceExportDenied('upgrade_required');
    if (message.includes('version_not_found')) throw new SourceExportDenied('not_found');
    // PostgREST has not picked the function up yet (stale schema cache), or a
    // server-side dependency of the RPC is missing (Postgres 42883).
    if (
      code === 'PGRST202' ||
      code === '42883' ||
      message.includes('Could not find the function') ||
      message.includes('does not exist')
    ) {
      throw new SourceExportDenied('unavailable');
    }
    if (message.includes('forbidden') || message.includes('not_authenticated')) {
      throw new SourceExportDenied('forbidden');
    }
    throw new SourceExportDenied('unknown');
  }


  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined;
  if (!row) throw new SourceExportDenied('not_found');

  return {
    versionId: String(row.version_id ?? versionId),
    projectId: String(row.project_id ?? ''),
    projectTitle: String(row.project_title ?? 'Untitled draft'),
    label: (row.label as string | null) ?? null,
    mainTex: String(row.main_tex ?? ''),
    bibSource: String(row.bib_source ?? ''),
    compileStatus: String(row.compile_status ?? 'unknown'),
    createdAt: String(row.created_at ?? ''),
  };
}

/** Filename-safe slug of a project title. */
export function slugifyTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || 'writer-draft';
}

/** Archive base name, e.g. `agentic-ai-v3`. */
export function exportBaseName(source: WriterSourceExport): string {
  const label = source.label ? slugifyTitle(source.label) : source.versionId.slice(0, 8);
  return `${slugifyTitle(source.projectTitle)}-${label}`;
}

/**
 * README shipped inside the archive. Carries the AI Act Art. 50 disclosure so
 * the AI-assistance labelling survives outside PaperVerse.
 */
export function buildReadme(source: WriterSourceExport): string {
  const hasBib = source.bibSource.trim().length > 0;
  return [
    `${source.projectTitle}`,
    '',
    `Version: ${source.label ?? source.versionId}`,
    `Created: ${source.createdAt}`,
    `Compile status at export: ${source.compileStatus}`,
    '',
    'Files',
    '-----',
    'main.tex        The complete LaTeX source. It compiles on its own.',
    hasBib
      ? 'references.bib  BibTeX database referenced by main.tex.'
      : 'No separate .bib file: the bibliography is embedded in main.tex as a',
    hasBib ? '' : 'thebibliography environment.',
    '',
    'Compiling',
    '---------',
    'PaperVerse compiles this source with Tectonic (TeX Live compatible).',
    'Any standard pdflatex/xelatex toolchain or Overleaf project works too.',
    '',
    'AI transparency (EU AI Act Art. 50)',
    '-----------------------------------',
    'Parts of this document were generated with AI assistance (Google Vertex AI,',
    'processed in the EU/EEA). Verify all content, numbers and citations before',
    'publication or submission.',
    '',
  ]
    .filter((line) => line !== '')
    .join('\n');
}

/** Builds the export archive as a Blob. */
export async function buildSourceArchive(source: WriterSourceExport): Promise<Blob> {
  const zip = new JSZip();
  zip.file('main.tex', source.mainTex);
  if (source.bibSource.trim().length > 0) {
    zip.file('references.bib', source.bibSource);
  }
  zip.file('README.txt', buildReadme(source));
  return zip.generateAsync({ type: 'blob' });
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetches and downloads a version's LaTeX source as a zip. Falls back to a
 * plain `main.tex` download when zipping is unavailable in the browser.
 */
export async function downloadWriterSource(versionId: string): Promise<void> {
  const source = await fetchWriterSource(versionId);
  const base = exportBaseName(source);
  try {
    const blob = await buildSourceArchive(source);
    triggerDownload(blob, `${base}.zip`);
  } catch {
    triggerDownload(new Blob([source.mainTex], { type: 'text/x-tex' }), `${base}.tex`);
  }
}
